<div class="faq-page">
  <!-- =========================================================
       Breadcrumb
  ========================================================== -->
  <nav class="breadcrumb" aria-label="Breadcrumb">
    <a class="breadcrumb__home" routerLink="/">
      <span aria-hidden="true">⌂</span>
    </a>

    <span class="breadcrumb__divider">/</span>

    <span class="breadcrumb__current">FAQs</span>
  </nav>

  <!-- =========================================================
       Hero
  ========================================================== -->
  <section class="faq-hero">
    <div class="faq-hero__background" aria-hidden="true">
      <span class="hero-orb hero-orb--one"></span>
      <span class="hero-orb hero-orb--two"></span>
      <span class="hero-grid"></span>
    </div>

    <div class="faq-hero__content">
      <span class="hero-pill">
        <span class="hero-pill__dot"></span>
        Help Centre
      </span>

      <h1>How can we help?</h1>

      <p>
        Find quick answers about booking, payments, your account, salon
        policies, and using gloget.
      </p>

      <div class="faq-search">
        <span class="faq-search__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="7"></circle>
            <path d="m20 20-3.7-3.7"></path>
          </svg>
        </span>

        <input
          type="search"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()"
          placeholder="Search questions, bookings, payments..."
          aria-label="Search frequently asked questions"
        />

        <button
          *ngIf="searchTerm"
          type="button"
          class="faq-search__clear"
          (click)="clearSearchTerm()"
          aria-label="Clear search"
        >
          ×
        </button>
      </div>

      <div class="faq-hero__meta">
        <span>{{ faqs.length }} helpful answers</span>
        <span class="faq-hero__meta-dot"></span>
        <span>For customers and salon owners</span>
      </div>
    </div>
  </section>

  <!-- =========================================================
       Main FAQ area
  ========================================================== -->
  <section class="faq-content">
    <div class="faq-category-strip" aria-label="FAQ categories">
      <button
        *ngFor="let category of categories"
        type="button"
        class="category-pill"
        [class.category-pill--active]="activeCategory === category.key"
        (click)="setCategory(category.key)"
      >
        {{ category.label }}
      </button>
    </div>

    <div class="faq-layout">
      <!-- =====================================================
           Sidebar
      ====================================================== -->
      <aside class="faq-sidebar">
        <div class="faq-side-card">
          <span class="section-kicker">Browse topics</span>

          <h2>Frequently asked questions</h2>

          <p>Choose a category or search across all help topics.</p>

          <div class="faq-side-nav">
            <button
              *ngFor="let category of categories"
              type="button"
              [class.faq-side-nav__item--active]="
                activeCategory === category.key
              "
              class="faq-side-nav__item"
              (click)="setCategory(category.key)"
            >
              <span>{{ category.label }}</span>

              <span class="faq-side-nav__count">
                {{ getCategoryCount(category.key) }}
              </span>
            </button>
          </div>
        </div>

        <div class="support-card">
          <div class="support-card__icon">?</div>

          <span class="section-kicker">Still need help?</span>

          <h3>We're here for you.</h3>

          <p>
            If you cannot find what you need, contact the gloget support team.
          </p>

          <a class="support-card__button" routerLink="/support">
            Contact support
            <span>→</span>
          </a>
        </div>
      </aside>

      <!-- =====================================================
           Results
      ====================================================== -->
      <main class="faq-results">
        <div class="faq-results__head">
          <div>
            <span class="section-kicker">
              {{
                activeCategory === "all"
                  ? "All questions"
                  : getActiveCategoryLabel()
              }}
            </span>

            <h2>
              {{
                searchTerm
                  ? 'Search results for "' + searchTerm + '"'
                  : "Everything you need to know."
              }}
            </h2>
          </div>

          <div class="result-count">
            <strong>{{ filteredFaqs.length }}</strong>
            <span>{{ filteredFaqs.length === 1 ? "answer" : "answers" }}</span>
          </div>
        </div>

        <div *ngIf="groupedFaqs.length; else noResults" class="faq-groups">
          <section *ngFor="let group of groupedFaqs" class="faq-group">
            <div class="faq-group__head">
              <span class="faq-group__eyebrow">{{ group.label }}</span>
              <span class="faq-group__count">{{ group.items.length }}</span>
            </div>

            <div class="faq-list">
              <article
                *ngFor="let faq of group.items"
                class="faq-item"
                [class.faq-item--open]="isFaqOpen(faq.id)"
              >
                <button
                  type="button"
                  class="faq-question"
                  (click)="toggleFaq(faq.id)"
                  [attr.aria-expanded]="isFaqOpen(faq.id)"
                  [attr.aria-controls]="'faq-answer-' + faq.id"
                >
                  <span>{{ faq.question }}</span>

                  <span class="faq-question__toggle" aria-hidden="true">
                    {{ isFaqOpen(faq.id) ? "−" : "+" }}
                  </span>
                </button>

                <div
                  *ngIf="isFaqOpen(faq.id)"
                  class="faq-answer"
                  [id]="'faq-answer-' + faq.id"
                >
                  <p>{{ faq.answer }}</p>
                </div>
              </article>
            </div>
          </section>
        </div>

        <ng-template #noResults>
          <div class="faq-empty">
            <div class="faq-empty__icon">?</div>

            <h3>No matching answers found</h3>

            <p>Try a different search term or return to all FAQ categories.</p>

            <button type="button" (click)="clearSearch()">View all FAQs</button>
          </div>
        </ng-template>
      </main>
    </div>
  </section>

  <!-- =========================================================
       Bottom CTA
  ========================================================== -->
  <section class="faq-bottom-cta">
    <div>
      <span class="section-kicker">Need a little more help?</span>

      <h2>Talk to the gloget team.</h2>

      <p>
        Questions about a booking, your account, or getting your salon listed?
        Our support team can point you in the right direction.
      </p>
    </div>

    <a routerLink="/support" class="faq-bottom-cta__button">
      Get in touch
      <span>→</span>
    </a>
  </section>
</div>
